<script lang="ts">
	import classNames from 'classnames';
	import dayjs from 'dayjs';
	import { NewsPageBody } from '../../components/NewsPageBody';
	import { NewsRoutes } from '../../constants/routes';
	import { NewsArticleJsonLdHead } from './NewsArticleJsonLdHead';
	import { Config } from '#lib/constants/config.js';
	import { trackOnwardClicks } from '#lib/features/analytics/utils/trackOnwardClicks/trackOnwardClicks.js';
	import { ArticleReadTracker } from '#lib/features/news/components/ArticleReadTracker/index.js';
	import { ArtistCardTag } from '#lib/features/news/components/NewsCard/ArtistCardTag/index.js';
	import { CategoryCardTag } from '#lib/features/news/components/NewsCard/CategoryCardTag/index.js';
	import { GeoCardTag } from '#lib/features/news/components/NewsCard/GeoCardTag/index.js';
	import { NewsletterWidget } from '#lib/features/news/components/NewsDataChart/NewsletterWidget/index.js';
	import { NewsMixpanelEvents } from '#lib/features/news/constants/mixpanel-events.js';
	import { useScrollDepthTracking } from '#lib/features/news/runes/useScrollDepthTracking/useScrollDepthTracking.svelte.js';
	import {
		ArticleContentType,
		getArticleMetadata,
	} from '#lib/features/news/utils/getArticleMetadata/getArticleMetadata.js';
	import { trackArticleRead } from '#lib/features/news/utils/trackArticleRead/trackArticleRead.js';
	import { page } from '$app/state';
	import { ShareIcon } from '$global/assets/icons/ShareIcon';
	import { Button } from '$global/components/Button';
	import { Container } from '$global/components/Container';
	import { createDialogStores } from '$global/components/Dialog';
	import { ShareDialog } from '$global/components/Share/ShareDialog';
	import { Txt } from '$global/components/Txt';
	import { ArticleGenerator } from '$global/features/article-generation/components/ArticleGenerator';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import { track } from '$global/utils/track';
	import type { NewsArticlePDPPageData } from '$routes/news/article/[slug]/types';

	interface Props {
		showGlobalNav?: boolean | undefined;
	}

	let { showGlobalNav = false }: Props = $props();

	let article = $derived(
		page.data.article as NewsArticlePDPPageData['article']
	);
	// Parsed into tokens by the server load, with the mobile page's article
	// links already rewritten (newsArticleServerLoad).
	let components = $derived(page.data.components);

	// The top level category is the broadest topic, so it leads the topic list.
	let articleMetadata = $derived(
		getArticleMetadata({
			contentType: ArticleContentType.HeniArticle,
			artists: article?.artist?.map((entry) => entry?.News_Artist_id?.name),
			topics: [
				article?.top_level_category,
				...(article?.categories?.map(
					(entry) => entry?.News_Category_id?.name
				) ?? []),
			],
			publishDate: article?.publication_date ?? article?.date_created,
		})
	);

	// The News→commerce bridge the ticket is built around (HB-967). The links that
	// cross into Editions, Artists or the Shop are authored in the CMS — inline
	// markdown links and CTA buttons — so there is no fixed call site to hang a
	// handler on; one delegated listener over the body catches them all.
	//
	// The article's artists ride along so a click can be attributed to an artist
	// even when the destination url alone does not name one.
	let articleOnwardClickParams = $derived({
		eventName: NewsMixpanelEvents.OnwardClick,
		sourceElement: 'article-body',
		fromSection: 'news' as const,
		artist: articleMetadata.featured_artists,
	});

	let handleClickPlatform = $derived((platform: string) => {
		track(NewsMixpanelEvents.ShareArticle, {
			id: article?.id,
			platform,
			...articleMetadata,
		});
	});

	let shareUrl = $derived(
		`${Config.Domain}${NewsRoutes.Article}/${article.slug}`
	);
	let shareIntro = $derived(`HENI News: ${article.title}`);

	const dialogStores = createDialogStores();

	const {
		states: { open },
	} = dialogStores;

	const handleOpenShareDialog = () => {
		if (showGlobalNav) {
			open.set(true);
		} else {
			(window.location as unknown as string) = `/news?shareApp=1&text=${article?.title}&url=${Config.Domain}${NewsRoutes.Article}/${article.slug}`;
		}
	};

	const dataCyPrefix = 'news-article';

	// The guard keeps the read event to one per article: the effect also reruns
	// when page.data recomputes the derived article without changing articles.
	let trackedArticleId: string | undefined;

	$effect(() => {
		if (article?.id && article.id !== trackedArticleId) {
			trackedArticleId = article.id;

			trackArticleRead({
				articleId: article.id,
				section: 'article',
				metadata: articleMetadata,
			});
		}
	});

	// Separate from the read effect above: this one has to re-run (and tear the
	// old observer down) whenever the article changes, so it reads article.id
	// reactively instead of guarding on it.
	$effect(() =>
		useScrollDepthTracking({
			articleId: article?.id,
			section: 'article',
		})
	);
</script>

<NewsArticleJsonLdHead />

<NewsPageBody
	class={classNames('bg-gray-0', {
		'pt-[3rem] min-[900px]:pt-[3rem] sm:pt-[3rem] lg:pt-[3.5rem]':
			!showGlobalNav,
	})}
>
	<Container dataCy={dataCyPrefix} class="mt-8 max-w-[720px] lg:mt-16">
		<div class="mb-6 border-b border-gray-200 pb-6">
			<Txt
				variant="h1"
				class="font-silka my-4 text-[1.5rem] leading-[2rem] font-[600] tracking-[0] text-gray-900 sm:text-[1.875rem] sm:leading-[2.25rem]"
			>
				{article.title}
			</Txt>

			<div class="flex items-center justify-between">
				<Txt variant="label3" class="text-gray-500">
					{#if article?.['time_to_read']}
						{article?.['time_to_read']} min read
					{/if}
					{#if article?.['time_to_read']}
						&nbsp;&middot;&nbsp;
					{/if}
					{#if dayjs(article?.['publication_date'])}
						{dayjs(article?.['publication_date']).format('DD MMM YYYY')}
					{:else if dayjs(article?.['date_created'])}
						{dayjs(article?.['date_created']).format('DD MMM YYYY')}
					{/if}
				</Txt>

				<Button
					dataCy={`${dataCyPrefix}-share`}
					aria-label="Share article"
					variant="secondary"
					size="sm"
					onclick={handleOpenShareDialog}
				>
					<ShareIcon class="h-5 w-5" />
				</Button>
			</div>
		</div>

		<ArticleReadTracker
			articleId={article.id}
			section="article"
			completionThreshold={100}
		>
			<!--
				Wraps only the generated body, so the delegated listener sees the
				CMS-authored links and not the share button or the tag row below —
				those are News-internal and would never cross sections anyway.
			-->
			<div use:trackOnwardClicks={articleOnwardClickParams}>
				<ArticleGenerator
					{getImageUrl}
					{components}
					enableContain
					title={article.title || ''}
				/>
			</div>
		</ArticleReadTracker>

		<hr class="my-10" />

		<div class="mb-10 flex flex-wrap gap-4">
			{#if article?.categories}
				{#each article?.categories as category (category?.News_Category_id?.id)}
					<CategoryCardTag
						dataCy={dataCyPrefix}
						category={`${category?.News_Category_id?.name}`}
						class={classNames({ 'pointer-events-none': !showGlobalNav })}
					/>
				{/each}
			{/if}

			{#each article?.artist || [] as artist (artist?.News_Artist_id?.id)}
				{#if artist?.News_Artist_id?.name}
					<ArtistCardTag
						showNewVersion={false}
						name={artist?.News_Artist_id?.name}
						dataCy={dataCyPrefix}
						class={classNames('h-[auto]', {
							'pointer-events-none': !showGlobalNav,
						})}
					/>
				{/if}
			{/each}

			{#if article?.geo_tag?.name}
				<GeoCardTag
					showNewVersion={false}
					name={article?.geo_tag?.name}
					dataCy={dataCyPrefix}
					class={classNames('h-[auto]', {
						'pointer-events-none': !showGlobalNav,
					})}
				/>
			{/if}
		</div>
	</Container>
	<Container dataCy={`${dataCyPrefix}-widget`}>
		<div class="pb-4 sm:my-8 sm:px-0">
			<NewsletterWidget />
		</div>
	</Container>
</NewsPageBody>

<ShareDialog
	colored
	onClickPlatform={handleClickPlatform}
	{dialogStores}
	link={shareUrl}
	intro={shareIntro}
	dataCy={dataCyPrefix}
	showText
	showTwitter={false}
/>
